import type { JSX } from 'react';
import type { WireArtifact } from '@logistics/contracts';
import { kindLabel, shortDay, whole } from '@/lib/format';

type Chart = Extract<WireArtifact, { kind: 'chart' }>;

/** Records per day as bars: the total as the headline, the busiest day in full accent. */
export function ChartArtifact({ artifact }: { artifact: Chart }): JSX.Element {
  const points = artifact.points;
  const total = points.reduce((sum, p) => sum + p.count, 0);
  const max = Math.max(1, ...points.map((p) => p.count));
  const what = artifact.recordKind === undefined ? 'records' : kindLabel(artifact.recordKind);
  const cols = { gridTemplateColumns: `repeat(${Math.max(1, points.length)}, minmax(0, 1fr))` };
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-2xl font-semibold tracking-[-.02em]">{whole(total)}</span>
        <span className="text-mute font-mono text-xs">
          {points.length} {points.length === 1 ? 'day' : 'days'}
        </span>
      </div>
      <div className="border-line bg-surf rounded-xl border px-3.5 pt-4 pb-2">
        <div className="grid h-[150px] items-end gap-1.5" style={cols}>
          {points.map((p) => (
            <div
              key={p.day}
              title={`${shortDay(p.day)}: ${p.count}`}
              className="flex h-full flex-col justify-end"
            >
              <div
                className="rounded-[4px_4px_2px_2px] transition-[height] duration-700"
                style={{
                  height: `${Math.max(2, (p.count / max) * 100)}%`,
                  background:
                    p.count === max
                      ? 'var(--acc)'
                      : 'color-mix(in oklch, var(--acc) 45%, transparent)',
                }}
              />
            </div>
          ))}
        </div>
        <div
          className="text-mute mt-2 grid gap-1.5 text-center font-mono text-[9.5px]"
          style={cols}
        >
          {points.map((p) => (
            <span key={p.day} className="truncate">
              {shortDay(p.day)}
            </span>
          ))}
        </div>
      </div>
      <div className="text-mute text-[13px] leading-normal">
        {what.charAt(0).toUpperCase() + what.slice(1)} per day (UTC), from the records the answer
        used.
      </div>
    </div>
  );
}
